import {
  DiagnosticsCard,
  DiagnosticsSection,
  StatusDot,
  formatTimestamp,
} from "./DiagnosticsPanelPrimitives";
import type { StatusTone } from "./DiagnosticsSettings.types";
import type {
  CodexReadinessBlockCode,
  CodexRuntimeReadinessFailureDetails,
} from "./codexRuntimeReadiness";

function readinessEventDetail(input: {
  readonly code: CodexReadinessBlockCode;
  readonly details: CodexRuntimeReadinessFailureDetails | null;
}): string {
  if (!input.details) return `Terminal readiness result: ${input.code}.`;
  const status =
    input.details.upstreamStatus === null
      ? "No upstream HTTP response completed."
      : `CMMD returned HTTP ${input.details.upstreamStatus}.`;
  return `Terminal readiness result: ${input.code} · ${input.details.phase} · ${status}`;
}

function EventRow({
  tone,
  time,
  title,
  detail,
}: {
  readonly tone: StatusTone;
  readonly time: string;
  readonly title: string;
  readonly detail: string;
}) {
  return (
    <div className="grid gap-3 px-4 py-3 text-sm md:grid-cols-[96px_20px_minmax(0,1fr)]">
      <div className="font-mono text-xs text-muted-foreground">{time}</div>
      <div className="flex items-center">
        <StatusDot tone={tone} />
      </div>
      <div className="min-w-0">
        <div className="font-medium text-foreground">{title}</div>
        <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{detail}</div>
      </div>
    </div>
  );
}

export function DiagnosticsEvents({
  diagnosticsError,
  cloudRuntimeErrors,
  readinessEvent,
}: {
  readonly diagnosticsError: string | undefined;
  readonly cloudRuntimeErrors: ReadonlyArray<{
    readonly label: string;
    readonly error: string;
    readonly at: string | null;
  }>;
  readonly readinessEvent: {
    readonly label: string;
    readonly runtimeId: string;
    readonly code: CodexReadinessBlockCode;
    readonly details: CodexRuntimeReadinessFailureDetails | null;
  } | null;
}) {
  return (
    <DiagnosticsSection
      title="Runtime events"
      id="diagnostics-events"
      description="Recent diagnostics and saved cloud runtime connection errors."
    >
      <DiagnosticsCard>
        <div className="divide-y divide-border/60">
          {diagnosticsError ? (
            <EventRow
              tone="error"
              time="now"
              title="Runtime diagnostics failed"
              detail={diagnosticsError}
            />
          ) : null}
          {readinessEvent ? (
            <EventRow
              tone="error"
              time="latest check"
              title={`${readinessEvent.label} Codex readiness blocked`}
              detail={readinessEventDetail(readinessEvent)}
            />
          ) : null}
          {cloudRuntimeErrors.map((event) => (
            <EventRow
              key={`${event.label}-${event.at ?? event.error}`}
              tone="error"
              time={formatTimestamp(event.at)}
              title={`${event.label} connection error`}
              detail={event.error}
            />
          ))}
          {!diagnosticsError && !readinessEvent && cloudRuntimeErrors.length === 0 ? (
            <EventRow
              tone="ready"
              time="now"
              title="No recent Environment errors"
              detail="No saved Environment connection failure or active diagnostics error is currently reported."
            />
          ) : null}
        </div>
      </DiagnosticsCard>
    </DiagnosticsSection>
  );
}
